import { isValidRoute } from "@/shared/config/routes/route-validation";

/**
 * Rules table — order doesn't matter (any match → minimal).
 * `exact` = string equality only; otherwise prefix-match (`/superuser` → also
 * matches `/superuser/foo`).
 *
 * Decomposed from a single `||`-chain to drop CC from 10 → 2.
 */
const MINIMAL_ROUTE_RULES: ReadonlyArray<{
	prefix: string;
	exact?: boolean;
}> = [
	{ prefix: "/", exact: true },
	{ prefix: "/alternate", exact: true },
	{ prefix: "/alternate2", exact: true },
	{ prefix: "/alternate3", exact: true },
	{ prefix: "/alternate4", exact: true },
	{ prefix: "/landing", exact: true },
	{ prefix: "/config", exact: true },
	{ prefix: "/account", exact: true },
	{ prefix: "/superuser" },
	{ prefix: "/login" },
	{ prefix: "/pending-org" },
	{ prefix: "/accept-invitation" },
	{ prefix: "/debug" },
	{ prefix: "/demo" },
	{ prefix: "/docs" },
	{ prefix: "/legal" },
	{ prefix: "/solutions" },
	{ prefix: "/playground", exact: true },
];

/**
 * Public/auth/marketing routes that never render the authenticated desktop
 * dock. Unlike `MINIMAL_ROUTE_RULES`, this deliberately excludes in-app pages
 * such as config, account, playground, and superuser.
 */
const DOCK_FREE_ROUTE_RULES: ReadonlyArray<{
	prefix: string;
	exact?: boolean;
}> = [
	{ prefix: "/", exact: true },
	{ prefix: "/alternate", exact: true },
	{ prefix: "/alternate2", exact: true },
	{ prefix: "/alternate3", exact: true },
	{ prefix: "/alternate4", exact: true },
	{ prefix: "/login" },
	{ prefix: "/pending-org" },
	{ prefix: "/accept-invitation" },
	{ prefix: "/debug" },
	{ prefix: "/demo" },
	{ prefix: "/docs" },
	{ prefix: "/legal" },
	{ prefix: "/solutions" },
];

function ruleMatches(
	rule: { prefix: string; exact?: boolean },
	pathname: string,
): boolean {
	if (rule.exact) {
		return pathname === rule.prefix;
	}
	return pathname === rule.prefix || pathname.startsWith(`${rule.prefix}/`);
}

/**
 * Check if a pathname is a minimal route (landing, auth, config, debug, demo).
 *
 * `null` returns `false` (not minimal) — callers expect a defined result.
 */
export function isMinimalRoute(pathname: string | null): boolean {
	if (pathname === null) {
		return false;
	}
	return MINIMAL_ROUTE_RULES.some((rule) => ruleMatches(rule, pathname));
}

/**
 * Check if a pathname is an invalid route (404)
 */
export function isInvalidRoute(pathname: string | null): boolean {
	return pathname !== null && !isValidRoute(pathname);
}

/**
 * Check if a pathname should show the app shell
 */
export function shouldShowShell(pathname: string | null): boolean {
	const isMinimal = isMinimalRoute(pathname);
	const isInvalid = isInvalidRoute(pathname);
	return !(isMinimal || isInvalid);
}

/**
 * Check if a pathname should show the authenticated desktop dock.
 *
 * This is intentionally independent from `shouldShowShell`: the latter keeps
 * controlling mobile bottom-nav clearance and the other bundled shell
 * behavior, while authenticated minimal-layout pages still receive the dock.
 */
export function shouldShowDock(pathname: string | null): boolean {
	if (pathname === null || isInvalidRoute(pathname)) {
		return false;
	}
	return !DOCK_FREE_ROUTE_RULES.some((rule) => ruleMatches(rule, pathname));
}

/**
 * Routes that scroll the document/window directly instead of the shared
 * app-shell scroller. These full-page marketing pages render OUTSIDE the
 * `SidebarProvider` shell (whose wrapper is `overflow: hidden; height: 100%`),
 * so the document can grow and window-scroll. Their CSS relaxes the shell's
 * html/body height lock (see `landing.css`'s `:has(.lp)` rules).
 */
const FULL_PAGE_SCROLL_ROUTES: ReadonlySet<string> = new Set([
	"/",
	"/alternate",
	"/alternate2",
	"/alternate3",
	"/alternate4",
	"/solutions/customer-support",
	"/solutions/sales",
]);

/**
 * Prefix-matched members of the same set.
 *
 * `/docs` is one page per path (`/docs/$`), so it cannot be enumerated. It
 * belongs here for the same reason the landing does: its layout is built for
 * DOCUMENT scroll — the sidebar and the table of contents are
 * `position: sticky` at `top: var(--docs-nav-h)` with `height: calc(100dvh -
 * var(--docs-nav-h))`, and the background is `background-attachment: fixed`.
 * Inside the shell's `height: 100%; overflow: hidden` cage those stuck to the
 * shell's scroller instead of the window while sizing themselves against the
 * viewport, so the article had nowhere to scroll.
 */
const FULL_PAGE_SCROLL_PREFIXES: readonly string[] = ["/docs"];

export function isFullPageScrollRoute(pathname: string | null): boolean {
	if (pathname === null) {
		return false;
	}
	if (FULL_PAGE_SCROLL_ROUTES.has(pathname)) {
		return true;
	}
	return FULL_PAGE_SCROLL_PREFIXES.some(
		(prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`),
	);
}
